import { ClubSearchSelect } from '~/pages/club/components/club-search-select';
import { useUnsavedChangesGuard } from '~/hooks/useUnsavedChangesGuard';
import { baseSchema, Schema, schema } from './create-person-form.schema';
import { useAuthentication } from '~/hooks/useAuthentication';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '~/components/Button/Button';
import { placeholder } from '~/utils/placeholder';
import { Grid } from '~/components/Grid/Grid';
import { Input } from '~/components/Input';
import { Resolver, useForm } from 'react-hook-form';

interface Props {
  onSubmit: (data: Partial<Schema>) => void;
  defaultValues?: Partial<Schema>;
  mode: 'create' | 'edit';
}

export function CreatePersonForm({ defaultValues, mode, onSubmit }: Props) {
  const { isSuperUser } = useAuthentication();

  const isBaseSchema = !isSuperUser && mode === 'edit';

  const { formState, handleSubmit, register, watch, trigger, setValue } = useForm<Schema>({
    resolver: zodResolver(isBaseSchema ? baseSchema : schema) as unknown as Resolver<Schema>,
    defaultValues,
  });

  const { dialog } = useUnsavedChangesGuard(formState);

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
      <Grid>
        <Grid.Item cols={{ sm: 12, lg: 6 }}>
          <Input.Group label="Förnamn" htmlFor="first_name" formState={formState}>
            <Input.Field type="text" {...register('first_name')} formState={formState} />
          </Input.Group>
        </Grid.Item>

        <Grid.Item cols={{ sm: 12, lg: 6 }}>
          <Input.Group label="Efternamn" htmlFor="last_name" formState={formState}>
            <Input.Field type="text" {...register('last_name')} formState={formState} />
          </Input.Group>
        </Grid.Item>
      </Grid>

      <Grid>
        <Grid.Item cols={{ sm: 12, lg: 6 }}>
          <Input.Group label="Personnummer" htmlFor="ssn" formState={formState}>
            <Input.Field
              type="text"
              {...register('ssn', { disabled: isBaseSchema })}
              formState={formState}
              placeholder={placeholder.ssn}
            />
          </Input.Group>
        </Grid.Item>

        <Grid.Item cols={{ sm: 12, lg: 6 }}>
          <Input.Group label="Förening" htmlFor="club" formState={formState}>
            <ClubSearchSelect
              disabled={isBaseSchema}
              placeholder="Sök på förening..."
              onChange={v => {
                setValue('club', v as [string, number], { shouldDirty: true });
                trigger('club');
              }}
              formState={formState}
              value={watch('club')}
              name="club"
            />
          </Input.Group>
        </Grid.Item>
      </Grid>

      {isSuperUser && (
        <Grid>
          <Grid.Item cols={{ sm: 12, lg: 1 }}>
            <Input.InlineGroup formState={formState} htmlFor="is_para" label="Paralicens">
              <Input.Checkbox {...register('is_para')} formState={formState} />
            </Input.InlineGroup>
          </Grid.Item>

          <Grid.Item cols={{ sm: 12, lg: 1 }}>
            <Input.InlineGroup formState={formState} htmlFor="is_ifn" label="IFN">
              <Input.Checkbox {...register('is_ifn')} formState={formState} />
            </Input.InlineGroup>
          </Grid.Item>
        </Grid>
      )}

      <div style={{ textAlign: 'right', paddingTop: 'var(--size-4)' }}>
        <Button type="submit">{{ create: 'Skapa', edit: 'Spara' }[mode]}</Button>
      </div>

      {dialog}
    </form>
  );
}
